'use client';
import React from 'react';
import { motion } from 'framer-motion';
import FadeScaleIn from './FadeScaleIn';

// Import images
const ASSET_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE_URL || '';
const imgResume = `${ASSET_BASE}/assets/web/benefits/b_resume_cover.webp`;
const imgInterview = `${ASSET_BASE}/assets/web/benefits/b_mock_interview.webp`;
const imgMentor = `${ASSET_BASE}/assets/web/benefits/b_mentor_connect.webp`;
const imgUpskilling = `${ASSET_BASE}/assets/web/benefits/b_ai_upskilling.webp`;
import { analytics } from '../services/analytics';
const imgCommunication = `${ASSET_BASE}/assets/web/benefits/b_communication.webp`;

const Benefits = () => {
    return (
        <section className="py-24 bg-white dark:bg-transparent relative overflow-hidden transition-colors duration-300">
            {/* Background Glows for Benefits */}
            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[30%] h-[60%] bg-brand-500/5 dark:bg-brand-500/[0.03] blur-[100px] rounded-full pointer-events-none" />
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[30%] h-[60%] bg-secondary-500/5 dark:bg-secondary-500/[0.03] blur-[100px] rounded-full pointer-events-none" />

            <div className="w-full px-4 md:container md:mx-auto relative z-10">

                {/* Section Header */}
                <div className="max-w-3xl mb-16 text-center lg:text-left mx-auto lg:mx-0">
                    <h2 className="text-brand-600 dark:text-brand-400 font-semibold tracking-wide uppercase text-xs mb-3">
                        Unified Career Platform
                    </h2>
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-5 leading-tight tracking-tight">
                        Build, Learn, and Land Your Next Opportunity
                    </h3>
                    <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl font-normal opacity-90">
                        We combine AI guidance, expert mentorship, and skill-building tools so you
                        can focus on growing your career without switching platforms.
                    </p>
                </div>

                {/* Bento Grid layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 lg:gap-8">

                    {/* Card 1: Resume & Cover Letter (Large, spans 3 cols) */}
                    <FadeScaleIn delay={0.1} className="lg:col-span-3">
                        <div
                            onClick={() => analytics.trackCardClick('Benefits', 'Resume & Cover Letter')}
                            className="p-2 border border-gray-100 dark:border-gray-800/50 rounded-2xl group hover:border-brand-500/30 dark:hover:border-brand-500/30 transition-all duration-500 cursor-pointer bg-white/50 dark:bg-gray-900/40 backdrop-blur-sm shadow-sm hover:shadow-2xl hover:-translate-y-1"
                        >
                            <div className="mb-6 rounded-xl overflow-hidden">
                                <img src={imgResume.src || imgResume} alt="Resume & Cover Letter" loading="lazy" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700" />
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 px-4 tracking-tight">Resume & Cover Letter</h4>
                            <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm px-4 mb-6">
                                Create professional resumes and cover letters quickly with AI guidance. Tailor them for the jobs you want and get noticed by employers.
                            </p>
                        </div>
                    </FadeScaleIn>

                    {/* Card 2: Mock Interview (Large, spans 3 cols) */}
                    <FadeScaleIn delay={0.2} className="lg:col-span-3">
                        <div
                            onClick={() => analytics.trackCardClick('Benefits', 'Mock Interview')}
                            className="p-2 border border-gray-100 dark:border-gray-800/50 rounded-2xl group hover:border-secondary-500/30 dark:hover:border-secondary-500/30 transition-all duration-500 cursor-pointer bg-white/50 dark:bg-gray-900/40 backdrop-blur-sm shadow-sm hover:shadow-2xl hover:-translate-y-1"
                        >
                            <div className="mb-6 rounded-xl overflow-hidden">
                                <img src={imgInterview.src || imgInterview} alt="Mock Interview" loading="lazy" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700" />
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 px-4 tracking-tight">Mock Interview</h4>
                            <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm px-4 mb-6">
                                Practice interviews with real questions and get feedback. Improve your answers, confidence, and presentation for interviews.
                            </p>
                        </div>
                    </FadeScaleIn>

                    {/* Card 3: 1:1 Mentor Connect (Medium, spans 2 cols) */}
                    <FadeScaleIn delay={0.3} className="lg:col-span-2">
                        <div
                            onClick={() => analytics.trackCardClick('Benefits', '1:1 Mentor Connect')}
                            className="p-2 border border-gray-100 dark:border-gray-800/50 rounded-2xl group hover:border-success-500/30 dark:hover:border-success-500/30 transition-all duration-500 cursor-pointer bg-white/50 dark:bg-gray-900/40 backdrop-blur-sm shadow-sm hover:shadow-2xl hover:-translate-y-1"
                        >
                            <div className="mb-6 rounded-xl overflow-hidden">
                                <img src={imgMentor.src || imgMentor} alt="1:1 Mentor Connect" loading="lazy" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700" />
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 px-4 tracking-tight">1:1 Mentor Connect</h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed px-4 mb-6">
                                Connect directly with career mentors. Get personalized advice on resumes, interviews, skill-building, and long-term growth.
                            </p>
                        </div>
                    </FadeScaleIn>

                    {/* Card 4: AI Upskilling Courses (Medium, spans 2 cols) */}
                    <FadeScaleIn delay={0.4} className="lg:col-span-2">
                        <div
                            onClick={() => analytics.trackCardClick('Benefits', 'AI Upskilling Courses')}
                            className="p-2 border border-gray-100 dark:border-gray-800/50 rounded-2xl group hover:border-warning-500/30 dark:hover:border-warning-500/30 transition-all duration-500 cursor-pointer bg-white/50 dark:bg-gray-900/40 backdrop-blur-sm shadow-sm hover:shadow-2xl hover:-translate-y-1"
                        >
                            <div className="mb-6 rounded-xl overflow-hidden">
                                <img src={imgUpskilling.src || imgUpskilling} alt="AI Upskilling Courses" loading="lazy" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700" />
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 px-4 tracking-tight">AI Upskilling Courses</h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed px-4 mb-6">
                                Learn the most relevant skills faster with AI-curated courses. Focus on what matters most to succeed in your target job.
                            </p>
                        </div>
                    </FadeScaleIn>

                    {/* Card 5: Communication Skills (Medium, spans 2 cols) */}
                    <FadeScaleIn delay={0.5} className="lg:col-span-2">
                        <div
                            onClick={() => analytics.trackCardClick('Benefits', 'Communication Skills')}
                            className="p-2 border border-gray-100 dark:border-gray-800/50 rounded-2xl group hover:border-error-500/30 dark:hover:border-error-500/30 transition-all duration-500 cursor-pointer bg-white/50 dark:bg-gray-900/40 backdrop-blur-sm shadow-sm hover:shadow-2xl hover:-translate-y-1"
                        >
                            <div className="mb-6 rounded-xl overflow-hidden">
                                <img src={imgCommunication.src || imgCommunication} alt="Communication Skills" loading="lazy" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700" />
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 px-4 tracking-tight">Communication Skills</h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed px-4 mb-6">
                                Improve how you speak, write, and present. Gain confidence in professional communication for interviews and meetings.
                            </p>
                        </div>
                    </FadeScaleIn>

                </div>
            </div>
        </section>
    );
};

export default Benefits;
